// Precision Wealth legal content (POPIA / FAIS).
// IMPORTANT: Wording marked DRAFT is pending compliance/legal approval. Fields marked
// "[To be confirmed]" are legal facts that must be supplied by Precision Wealth / Liberty
// compliance before this content is represented as final. Do not present as approved until reviewed.
import { COMPLIANCE_FULL, LIBERTY_FSP } from './compliance';

export const LEGAL_STATUS = 'DRAFT'; // set to 'APPROVED' only after human compliance sign-off
export const LEGAL_LAST_UPDATED = '[To be confirmed]';

// Central, editable legal facts. Replace bracketed placeholders once confirmed.
export const LEGAL_FACTS = {
  responsibleParty: 'Precision Wealth (a division of the Hereford Group)',
  informationOfficer: '[To be confirmed — Information Officer name & email]',
  contactEmail: 'info@precisionwealth.co.za',
  website: 'https://precisionwealth.co.za',
  fspRelationship: COMPLIANCE_FULL,
  fspNumber: LIBERTY_FSP,
  complaintsContact: '[To be confirmed — complaints contact]',
  faisOmbud: 'FAIS Ombud — Tel 012 762 5000 / 012 470 9080, info@faisombud.co.za, www.faisombud.co.za',
  informationRegulator: 'Information Regulator (South Africa) — inforeg@justice.gov.za, www.inforegulator.org.za',
  piCover: '[To be confirmed — professional indemnity cover]',
  aiProvider: '[To be confirmed — AI model/provider and data handling]',
  analyticsProviders: 'Google Analytics (only if you accept analytics cookies).',
  processors: '[To be confirmed — list of processors/subprocessors and their locations]',
  internationalTransfers: '[To be confirmed — whether personal data is processed outside South Africa]',
  retention: '[To be confirmed — retention periods per data category]',
};

export const PRIVACY = {
  title: 'Privacy Notice',
  intro: 'This notice explains how Precision Wealth processes your personal information under the Protection of Personal Information Act (POPIA).',
  sections: [
    { h: 'Responsible party', b: `${LEGAL_FACTS.responsibleParty}. Contact: ${LEGAL_FACTS.contactEmail}.` },
    { h: 'Information Officer', b: LEGAL_FACTS.informationOfficer },
    { h: 'Information we collect', b: 'Account details (name, email, age), information you submit in consultation and contact forms (name, email, phone, message), learning activity (courses, quizzes, progress), any figures you enter into financial-health and budgeting tools, and messages you send to the Precision Assistant.' },
    { h: 'Why we process it', b: 'To provide financial education, respond to consultation requests, personalise your learning, operate the platform, and (only with your separate consent) send you marketing.' },
    { h: 'Financial-health and budget data', b: 'Any amounts you enter into our tools are used to generate your on-screen education and estimates. This data is not sold. See retention below.' },
    { h: 'The Precision Assistant (AI)', b: `Messages you send to the AI Assistant are processed to generate educational responses. ${LEGAL_FACTS.aiProvider}. The Assistant provides general education only and never personal financial advice.` },
    { h: 'Analytics and cookies', b: `${LEGAL_FACTS.analyticsProviders} Optional analytics only run after you accept them in the cookie banner, and you can change your choice at any time.` },
    { h: 'Processors and international transfers', b: `${LEGAL_FACTS.processors} ${LEGAL_FACTS.internationalTransfers}` },
    { h: 'Retention', b: LEGAL_FACTS.retention },
    { h: 'Your rights', b: 'You may request access to, correction of, or deletion of your personal information, object to processing, and withdraw marketing consent. Use Profile → Privacy & Data, or contact us.' },
    { h: 'Complaints', b: `${LEGAL_FACTS.complaintsContact}. You may also complain to the ${LEGAL_FACTS.informationRegulator}` },
  ],
};

export const TERMS = {
  title: 'Terms of Use',
  intro: 'These terms govern your use of the Precision Wealth education platform.',
  sections: [
    { h: 'Education only, not advice', b: 'The platform provides general financial and business education. It does not constitute financial, tax, legal or investment advice under the FAIS Act. Personal advice is provided only by a qualified adviser within a formal advisory engagement.' },
    { h: 'Eligibility', b: 'You must be 18 years or older to create an account.' },
    { h: 'Your account', b: 'Keep your login details secure. You are responsible for activity under your account.' },
    { h: 'Acceptable use', b: 'Do not misuse the platform, attempt to breach security, or use it for unlawful purposes.' },
    { h: 'No guarantees', b: 'Estimates, projections and illustrations are for education only and are not guarantees of future results.' },
    { h: 'Changes', b: 'We may update these terms; material changes will be reflected here with a revised date.' },
    { h: 'Contact', b: LEGAL_FACTS.contactEmail },
  ],
};

export const DISCLOSURES = {
  title: 'Regulatory Disclosures',
  intro: 'FAIS and related disclosures for Precision Wealth and its advisers.',
  sections: [
    { h: 'Legal entity & FSP relationship', b: LEGAL_FACTS.fspRelationship },
    { h: 'Authorised categories & supervision', b: '[To be confirmed — product categories the advisers are authorised for, and any supervision status]' },
    { h: 'Professional indemnity cover', b: LEGAL_FACTS.piCover },
    { h: 'Conflicts of interest', b: '[To be confirmed — conflict of interest management policy summary]' },
    { h: 'Complaints', b: LEGAL_FACTS.complaintsContact },
    { h: 'FAIS Ombud', b: LEGAL_FACTS.faisOmbud },
    { h: 'Product supplier', b: 'Advisers are representatives of Liberty Group Limited (FSP 2409).' },
  ],
};

export const LEGAL_DOCS = { privacy: PRIVACY, terms: TERMS, disclosures: DISCLOSURES };

// One concise pre-collection line to show immediately before data submission.
export const PRECOLLECTION_NOTICE =
  'We process your details under POPIA to respond to you. We will not use them for marketing unless you opt in.';
